import { useLocation } from '@tanstack/react-router';
import { area, bbox, bboxPolygon, buffer, centroid, clustersDbscan, feature } from '@turf/turf';
import type { BBox, Feature, FeatureCollection, MultiPolygon, Point, Polygon } from 'geojson';
import type { GeoJSONSource } from 'mapbox-gl';
import { useEffect, useMemo } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import colors, {
  PROJECT_AREA_HOVER_LINE_COLOR,
  PROJECT_AREA_LINE_COLOR,
  PROJECT_AREA_SELECTED_LINE_COLOR,
} from '~/colors';
import {
  getOrgStateOrUsInitialViewport,
  getProjectsFallbackBbox,
} from '~/components2/Map/helpers/defaultInitialViewport';
import { measureStatus } from '~/components2/Map/Widgets/Measure/MeasureSurface';
import splitViewStore from '~/components2/Map/Widgets/SplitView/store';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import { homeMapStore } from '../store';

const CLUSTER_ZOOM = 11;

interface ProjectPolygonProps {
  polygons: FeatureCollection<Polygon | MultiPolygon>;
}

export default function ProjectPolygons({ polygons }: ProjectPolygonProps) {
  const location = useLocation();
  const { isMeasuring } = useSnapshot(measureStatus);
  const { projectLayer } = useSnapshot(layerStore);
  const { current: map } = MapGL.useMap();
  const { popupParams, hoverProjectId } = useSnapshot(homeMapStore);
  const { currentOrganizationDetail } = useSnapshot(rootStore) as typeof rootStore;
  const currentOrganizationState = currentOrganizationDetail.isLoading ? null : currentOrganizationDetail.state;
  const { splitView, previous: splitViewPreviousState } = useSnapshot(splitViewStore);

  useEffect(() => {
    homeMapStore.isPopupDisabled = isMeasuring === true;
  }, [isMeasuring]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    function projectPolygonClick({ features, lngLat }: mapboxgl.MapMouseEvent) {
      if (homeMapStore.isPopupDisabled || homeMapStore.inNewProjectMode) return;

      // Sort selected features by smallest area first - this makes it easier for users to select the intended project when projects overlap
      features?.sort((featureA, featureB) => area(featureA.geometry) - area(featureB.geometry));

      // show popup for first feature in the list (aka the smallest project)
      if (features?.[0]) {
        const {
          id,
          projectId,
          projectName,
          projectManager,
          projectPhase,
          projectBillingTier,
          projectAddress,
          projectYear,
        } = features[0].properties!;
        homeMapStore.setPopupParams({
          lngLat: [lngLat.lng, lngLat.lat],
          id,
          projectId,
          projectName,
          projectManager: projectManager ?? '',
          projectPhase: projectPhase ?? '',
          projectYear: projectYear ?? '',
          projectBillingTier: projectBillingTier ?? '',
          projectAddress: projectAddress ?? '',
        });
      }
    }

    function projectPolygonHover({ features }: mapboxgl.MapMouseEvent) {
      if (homeMapStore.inNewProjectMode) return;

      // Sort selected features by smallest area first - this makes it easier for users to select the intended project when projects overlap
      features?.sort((featureA, featureB) => area(featureA.geometry) - area(featureB.geometry));
      homeMapStore.setHoverProjectId(features?.[0].properties?.projectId);
    }

    function onClusterClick(e: mapboxgl.MapMouseEvent) {
      const map = e.target;
      // inspired by https://docs.mapbox.com/mapbox-gl-js/example/cluster/
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['cluster'],
      });
      if (features && features.length > 0) {
        (map.getSource('projectCentroids') as GeoJSONSource).getClusterExpansionZoom(
          features[0].properties!['cluster_id'],
          (_, zoom) => {
            map.easeTo({
              // @ts-expect-error
              center: features![0].geometry.coordinates,
              zoom: zoom || undefined,
            });
          },
        );
      }
    }

    function onUnclusteredPointClick(e: mapboxgl.MapMouseEvent) {
      const map = e.target;
      setTimeout(() => {
        map.flyTo({
          center: e.lngLat,
          zoom: CLUSTER_ZOOM + 2,
        });
      }, 100); // I'm not sure why but this does not work without the setTimeout
    }

    map?.on('click', 'project-polygon', projectPolygonClick);
    map?.on('mouseenter', 'project-polygon', projectPolygonHover);
    map?.on('mousemove', 'project-polygon', projectPolygonHover);
    map?.on('mouseleave', 'project-polygon', projectPolygonHover);
    map?.on('click', 'cluster', onClusterClick);
    map?.on('click', 'unclustered-point', onUnclusteredPointClick);

    return () => {
      map?.off('click', onClusterClick);
      map?.off('click', onUnclusteredPointClick);
      map?.off('click', 'project-polygon', projectPolygonClick);
      map?.off('mouseenter', 'project-polygon', projectPolygonHover);
      map?.off('mousemove', 'project-polygon', projectPolygonHover);
      map?.off('mouseleave', 'project-polygon', projectPolygonHover);
      homeMapStore.setPopupParams(undefined);
    };
  }, []);

  const polygonCentroids: FeatureCollection<Point> = useMemo(() => {
    const centroids = polygons.features
      .filter((p) => p.properties!.sandboxProject !== true)
      .map((p) => {
        return centroid(p.geometry, {
          properties: {
            feature: feature(p.geometry),
            ...(!!p.properties?.circleCenter && {
              circleCenter: p.properties.circleCenter,
            }),
          },
        });
      });
    return { type: 'FeatureCollection', features: centroids };
  }, [polygons]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    // this prevents centroid zoom if navigated to from projectDetails page
    // also prevents zoom if user is navigating to/from split view
    // also prevents zoom if we've already done the initial fitBounds (e.g., after basemap change)

    if (
      currentOrganizationDetail.isLoading ||
      homeMapStore.hasInitialFitBounds ||
      (location.state && (location.state as any)['from']) ||
      splitView === true ||
      splitViewPreviousState === true
    ) {
      // reset state so it does not affect future page loads
      // history.replace({ state: {} });
      return;
    }

    // Null until clustering finds bounds, so we can fall back to the org's state capitol / US center.
    let projectBbox: BBox | null = null;
    let allProjectsBboxCache: BBox | null | undefined;
    const getAllProjectsBbox = (): BBox | null => {
      if (allProjectsBboxCache === undefined) {
        allProjectsBboxCache = getProjectsFallbackBbox(polygons);
      }
      return allProjectsBboxCache;
    };

    if (polygons.features.length > 1 && polygonCentroids.features.length > 0) {
      // get centroids

      const clusteredPoints: FeatureCollection<Point, any> = clustersDbscan(
        polygonCentroids,
        100, // in km
        {
          minPoints: 1,
        },
      );

      // If there is only 1 cluster, manually add the id
      clusteredPoints.features.forEach((p) => {
        if (!p.properties.cluster) {
          p.properties.cluster = 0;
        }
      });

      const results: { [key: string]: Feature[] } = {};
      clusteredPoints.features.forEach((p) => {
        if (p.properties.cluster !== undefined) {
          if (results[p.properties.cluster.toString()]) {
            results[p.properties.cluster.toString()].push(p);
          } else {
            results[p.properties.cluster.toString()] = [p];
          }
        }
      });

      const clusterNames = Object.keys(results);

      if (clusterNames.length > 0) {
        let mostPopulatedCluster = clusterNames[0];
        let mostPopulatedClusterCount = results[clusterNames[0]].length;
        clusterNames.forEach((key) => {
          if (results[key].length > mostPopulatedClusterCount) {
            mostPopulatedCluster = key;
            mostPopulatedClusterCount = results[key].length;
          }
        });

        projectBbox = bbox({
          type: 'FeatureCollection',
          features: results[mostPopulatedCluster].map((f) => f.properties!.feature),
        });

        if (clusterNames.length === 1) {
          const projectBboxPolygon = bboxPolygon(projectBbox);
          projectBbox = bbox(buffer(projectBboxPolygon, 100)!);
        }
      }
    } else if (polygons.features.length === 1) {
      projectBbox = getAllProjectsBbox();
    }

    if (projectBbox) {
      map?.fitBounds(projectBbox as [number, number, number, number]);
    } else {
      // CG-680: frame available projects before the state/US fallback.
      const allProjectsBbox = getAllProjectsBbox();
      if (allProjectsBbox) {
        map?.fitBounds(allProjectsBbox as [number, number, number, number]);
      } else {
        const fallbackViewport = getOrgStateOrUsInitialViewport();
        map?.flyTo({
          center: [fallbackViewport.longitude, fallbackViewport.latitude],
          zoom: fallbackViewport.zoom,
        });
      }
    }
    homeMapStore.setHasInitialFitBounds(true);
  }, [polygonCentroids, splitView, map, currentOrganizationDetail.isLoading, currentOrganizationState]);

  // if project layer is turned off in the layer store, don't render polygons or clusters
  if (!projectLayer) {
    return null;
  }

  if (polygons) {
    // Reversing features so that the most recently created project has the highest z-index on the map.
    const reversedFeatures = polygons.features.toReversed();

    const getProjectColor = (f: (typeof reversedFeatures)[0]) => {
      switch (f.properties?.projectId) {
        case popupParams?.projectId:
          return PROJECT_AREA_SELECTED_LINE_COLOR;
        case hoverProjectId:
          return PROJECT_AREA_HOVER_LINE_COLOR;
        default:
          return PROJECT_AREA_LINE_COLOR;
      }
    };

    return (
      <>
        <MapGL.Source
          id="projectCentroids"
          type="geojson"
          data={polygonCentroids}
          cluster={true}
          clusterMaxZoom={CLUSTER_ZOOM}
          clusterRadius={50}
        >
          <MapGL.Layer
            id="cluster"
            type="circle"
            source={'projectCentroids'}
            filter={['has', 'point_count']}
            paint={{
              'circle-color': colors.white,
              'circle-stroke-color': PROJECT_AREA_LINE_COLOR,
              'circle-stroke-width': 2.5,
              'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
              'circle-emissive-strength': 1,
            }}
          />
          <MapGL.Layer
            id="cluster-count"
            type="symbol"
            source={'projectCentroids'}
            filter={['has', 'point_count']}
            layout={{
              'text-field': ['get', 'point_count_abbreviated'],
              'text-size': 14,
            }}
          />

          <MapGL.Layer
            id="unclustered-point"
            type="circle"
            source={'projectCentroids'}
            filter={['!', ['has', 'point_count']]}
            paint={{
              'circle-color': colors.white,
              'circle-radius': 10,
              'circle-stroke-color': PROJECT_AREA_LINE_COLOR,
              'circle-stroke-width': 2.5,
              'circle-emissive-strength': 1,
            }}
            maxzoom={CLUSTER_ZOOM + 1}
          />
          <MapGL.Layer
            id="unclustered-count"
            type="symbol"
            source={'projectCentroids'}
            filter={['!', ['has', 'point_count']]}
            layout={{
              'text-field': '1',
              'text-size': 14,
            }}
            maxzoom={CLUSTER_ZOOM + 1}
          />
        </MapGL.Source>

        <MapGL.Source
          id="projectPolygons"
          type="geojson"
          data={{
            type: 'FeatureCollection',
            features: reversedFeatures.map((f) => ({
              type: 'Feature',
              geometry: f.geometry,
              properties: {
                ...f.properties,
                color: getProjectColor(f),
              },
            })),
          }}
        >
          <MapGL.Layer
            type="line"
            paint={{
              'line-color': ['get', 'color'],
              'line-width': 4,
              'line-dasharray': [3, 1],
              'line-emissive-strength': 1,
            }}
            minzoom={CLUSTER_ZOOM + 1}
          />
          <MapGL.Layer
            id="project-polygon"
            type="fill"
            paint={{
              'fill-color': ['get', 'color'],
              'fill-opacity': 0.05,
              'fill-emissive-strength': 1,
            }}
            minzoom={CLUSTER_ZOOM + 1}
          />
        </MapGL.Source>

        <MapGL.Source id="circle-project-centers" type="geojson" data={polygonCentroids}>
          <MapGL.Layer
            id="center"
            type="circle"
            source="circle-project-centers"
            filter={['has', 'circleCenter']}
            minzoom={CLUSTER_ZOOM + 1}
            paint={{
              'circle-color': PROJECT_AREA_LINE_COLOR,
              'circle-radius': 5,
              'circle-stroke-color': colors.white,
              'circle-stroke-width': 2,
              'circle-emissive-strength': 1,
            }}
          />
        </MapGL.Source>
      </>
    );
  } else {
    return null;
  }
}
